import React, { Component } from 'react';
import { bool, func, node, number, object, string } from 'prop-types';
import classNames from 'classnames';
import { injectIntl, intlShape } from '../../util/reactIntl';

import { OutsideClickHandler, Icon } from '../../components';
import { FilterForm } from '../../forms';

import css from './FilterPopup.module.css';

const KEY_CODE_ESCAPE = 27;

const STATE_SUBMIT = 'submit';
const STATE_CLEAR = 'clear';
const STATE_CANCEL = 'cancel';

const ListingButton = props => {
  const { isOpen, label, labelText, ...rest } = props;

  const wrapperClasses = classNames(css.listingButtonWrapper, {
    [css.listingButtonWrapperOpen]: isOpen,
  });
  const iconClasses = classNames(css.listingButtonIcon, {
    [css.listingButtonIconOpen]: isOpen,
  });
  return (
    <button className={css.listingButton} {...rest}>
      <div className={wrapperClasses}>
        <div className={css.listingButtonLabelWrapper}>
          <label className={css.listingButtonLabel}>{labelText}</label>
          <span className={css.listingButtonLabelText}>{label}</span>
        </div>
        <Icon name="chevronDown" className={iconClasses} />
      </div>
    </button>
  );
};

class FilterPopup extends Component {
  constructor(props) {
    super(props);

    this.state = { isOpen: false };
    this.filter = null;
    this.filterContent = null;

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.toggleOpen = this.toggleOpen.bind(this);
    this.positionStyleForContent = this.positionStyleForContent.bind(this);
  }

  handleSubmit(values) {
    const { onSubmit } = this.props;
    this.setState({ isOpen: false });
    onSubmit(values, STATE_SUBMIT);
  }

  handleChange(values) {
    const { onChange } = this.props;
    if (onChange) {
      onChange(values);
    }
  }

  handleClear() {
    const { onSubmit, onClear } = this.props;
    this.setState({ isOpen: false });

    if (onClear) {
      onClear();
    }

    onSubmit(null, STATE_CLEAR);
  }

  handleCancel() {
    const { onSubmit, onCancel, initialValues } = this.props;
    this.setState({ isOpen: false });

    if (onCancel) {
      onCancel();
    }

    onSubmit(initialValues, STATE_CANCEL);
  }

  handleBlur() {
    this.setState({ isOpen: false });
  }

  handleKeyDown(e) {
    // Escape only ever closes (toggleOpen(false) toggled, so it opened a closed popup)
    if (e.key === 'Escape' || e.keyCode === KEY_CODE_ESCAPE) {
      this.setState({ isOpen: false });
    }
  }

  toggleOpen(enforcedState) {
    if (enforcedState) {
      this.setState({ isOpen: enforcedState });
    } else {
      this.setState(prevState => ({ isOpen: !prevState.isOpen }));
    }
  }

  positionStyleForContent() {
    if (this.filter && this.filterContent) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - this.filter.getBoundingClientRect().right;
      const labelWidth = this.filter.offsetWidth;
      const contentWidth = this.filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const contentPlacementOffset = this.props.contentPlacementOffset;

      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  }

  render() {
    const {
      rootClassName,
      className,
      popupClassName,
      id,
      label,
      labelText,
      labelMaxWidth,
      isSelected,
      children,
      initialValues,
      keepDirtyOnReinitialize,
      contentPlacementOffset,
      showAsListingButton,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);
    const popupClasses = classNames(css.popup, { [css.isOpen]: this.state.isOpen });
    const outsideClickHandlerClasses = classNames(css.outsideClickHandler, {
      [css.outsideClickHandlerOpen]: this.state.isOpen,
    });
    const popupSizeClasses = popupClassName || css.popupSize;
    const labelStyles = isSelected ? css.labelSelected : css.label;
    const labelMaxWidthMaybe = labelMaxWidth ? { maxWidth: `${labelMaxWidth}px` } : {};
    const labelMaxWidthStyles = labelMaxWidth ? css.labelEllipsis : null;
    const contentStyle = this.positionStyleForContent();

    return (
      <OutsideClickHandler className={outsideClickHandlerClasses} onOutsideClick={this.handleBlur}>
        <div
          className={classes}
          onKeyDown={this.handleKeyDown}
          ref={node => {
            this.filter = node;
          }}
        >
          {showAsListingButton ? (
            <ListingButton
              label={label}
              labelText={labelText}
              isOpen={this.state.isOpen}
              onClick={() => this.toggleOpen()}
            />
          ) : (
            <button
              type="button"
              className={labelStyles}
              style={labelMaxWidthMaybe}
              onClick={() => this.toggleOpen()}
              aria-expanded={this.state.isOpen}
              aria-controls={id}
            >
              <span className={labelMaxWidthStyles}>{label}</span>
              <Icon name="chevronDown" size={12} className={css.caret} />
            </button>
          )}
          <div
            id={id}
            className={classNames(popupClasses, popupSizeClasses)}
            ref={node => {
              this.filterContent = node;
            }}
            style={contentStyle}
          >
            {this.state.isOpen ? (
              <FilterForm
                id={`${id}.form`}
                showAsPopup
                contentPlacementOffset={contentPlacementOffset}
                initialValues={initialValues}
                keepDirtyOnReinitialize={keepDirtyOnReinitialize}
                onSubmit={this.handleSubmit}
                onChange={this.handleChange}
                onCancel={this.handleCancel}
                onClear={this.handleClear}
              >
                {children}
              </FilterForm>
            ) : null}
          </div>
        </div>
      </OutsideClickHandler>
    );
  }
}

FilterPopup.defaultProps = {
  rootClassName: null,
  className: null,
  popupClassName: null,
  onChange: null,
  initialValues: null,
  keepDirtyOnReinitialize: false,
  contentPlacementOffset: 0,
  liveEdit: false,
  label: null,
  labelText: null,
  labelMaxWidth: null,
  showAsListingButton: false,
};

FilterPopup.propTypes = {
  rootClassName: string,
  className: string,
  popupClassName: string,
  id: string.isRequired,
  onSubmit: func.isRequired,
  onChange: func,
  initialValues: object,
  keepDirtyOnReinitialize: bool,
  contentPlacementOffset: number,
  label: string.isRequired,
  labelText: string.isRequired,
  labelMaxWidth: number,
  isSelected: bool.isRequired,
  showAsListingButton: bool.isRequired,
  children: node.isRequired,

  // form injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(FilterPopup);
